import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '@/components/Layout/Layout';
import MainHeading from '@/components/common/MainHeading';
import CardLayout from '@/components/Layout/CardLayout';
import { FormService } from '@/services/FormService';
import { IFormData, IFormColumn, IFormSection, InputType } from '@/interfaces/form.model';
import { showToastNotification } from '@/utility/helper';
import FullPageLoader from '@/components/loader/FullPageLoader';
import Label from '@/components/ui/Label';
import PrimaryInput from '@/components/ui/PrimaryInput';
import { Badge, Container, Row, Col } from 'reactstrap';
import moment from 'moment';
// import { useNavigate } from 'react-router-dom';

const FormView: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    // const navigate = useNavigate();
    const [formData, setFormData] = useState<IFormData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [formValues, setFormValues] = useState<{ [key: string]: any }>({});
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    console.log("formValues", formValues);

    const getFormDetails = useCallback(async () => {
        if (!id) return;
        try {
            setIsLoading(true);
            const response = await FormService.getFormById(id);
            if (response?.status) {
                console.log(response);
                // Handle both direct object and nested officeForm response
                const fetchedData = (response.data as any)?.officeForm?.[0] || response.data;
                setFormData(fetchedData);
            } else {
                showToastNotification('danger', response?.message || 'Failed to fetch form details');
            }
        } catch (error: any) {
            showToastNotification('danger', error?.message || 'Error loading form');
        } finally {
            setIsLoading(false);
        }
    }, [id]);

    useEffect(() => {
        getFormDetails();
    }, [getFormDetails]);

    const handleInputChange = (identifier: string, value: any) => {
        setFormValues(prev => ({
            ...prev,
            [identifier]: value
        }));
        // Clear error when user types
        if (errors[identifier]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[identifier];
                return newErrors;
            });
        }
    };

    const validateForm = (sections: IFormSection[]): boolean => {
        const newErrors: { [key: string]: string } = {};

        sections.forEach(section => {
            section.columns?.forEach(column => {
                if (column.required) {
                    const identifier = (column as any).id || (column as any)._id || column.columnName || '';
                    const value = formValues[identifier];
                    if (!value || (Array.isArray(value) && value.length === 0)) {
                        newErrors[identifier] = `${column.label} is required`;
                    }
                }
            });
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const renderColumn = (column: IFormColumn) => {
        const type = (column.columnDatatype || 'text').toLowerCase();
        const identifier = (column as any).id || (column as any)._id || column.columnName || '';
        const value = formValues[identifier] || '';
        const error = errors[identifier];

        switch (type) {
            case 'textarea':
                return (
                    <div className="mb-4">
                        <Label id={`label-${column.id}`} required={column.required}>{column.label}</Label>
                        <textarea
                            className={`form-control ${error ? 'is-invalid' : ''}`}
                            rows={4}
                            placeholder={column.placeholder || `Enter ${column.label}`}
                            value={value}
                            onChange={(e) => handleInputChange(identifier, e.target.value)}
                        />
                        {error ? <div className="invalid-feedback d-block">{error}</div> : (column.helpText && <small className="text-muted mt-1 d-block">{column.helpText}</small>)}
                    </div>
                );
            case 'select':
                return (
                    <div className="mb-4">
                        <Label id={`label-${column.id}`} required={column.required}>{column.label}</Label>
                        <select
                            className={`form-select form-control ${error ? 'is-invalid' : ''}`}
                            value={value}
                            onChange={(e) => handleInputChange(identifier, e.target.value)}
                        >
                            <option value="">Select {column.label}</option>
                            {column.options?.map((opt, idx) => (
                                <option key={idx} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                        {error ? <div className="invalid-feedback d-block">{error}</div> : (column.helpText && <small className="text-muted mt-1 d-block">{column.helpText}</small>)}
                    </div>
                );
            case 'radio':
            case 'checkbox':
                return (
                    <div className="mb-4">
                        <Label id={`label-${column.id}`} required={column.required} labelClassname="d-block">{column.label}</Label>
                        <div className={`d-flex flex-wrap gap-3 mt-2 ${error ? 'is-invalid p-2 border rounded' : ''}`} style={error ? { borderColor: '#dc3545' } : {}}>
                            {column.options?.map((opt, idx) => {
                                const isChecked = type === 'checkbox'
                                    ? (Array.isArray(value) ? value.includes(opt.value) : false)
                                    : value === opt.value;

                                return (
                                    <div key={idx} className="form-check mr-3">
                                        <input
                                            className="form-check-input"
                                            type={type}
                                            name={column.columnName}
                                            id={`${column.id}-${idx}`}
                                            checked={isChecked}
                                            onChange={(e) => {
                                                if (type === 'checkbox') {
                                                    const currentValues = Array.isArray(value) ? [...value] : [];
                                                    if (e.target.checked) {
                                                        currentValues.push(opt.value);
                                                    } else {
                                                        const index = currentValues.indexOf(opt.value);
                                                        if (index > -1) currentValues.splice(index, 1);
                                                    }
                                                    handleInputChange(identifier, currentValues);
                                                } else {
                                                    handleInputChange(identifier, opt.value);
                                                }
                                            }}
                                        />
                                        <label className="form-check-label" htmlFor={`${column.id}-${idx}`}>
                                            {opt.label}
                                        </label>
                                    </div>
                                );
                            })}
                        </div>
                        {error ? <div className="invalid-feedback d-block">{error}</div> : (column.helpText && <small className="text-muted mt-1 d-block">{column.helpText}</small>)}
                    </div>
                );
            case 'file':
                return (
                    <div className="mb-4">
                        <Label id={`label-${column.id}`} required={column.required}>{column.label}</Label>
                        <input
                            type="file"
                            className={`form-control ${error ? 'is-invalid' : ''}`}
                            onChange={(e) => handleInputChange(identifier, e.target.files?.[0])}
                        />
                        {error ? <div className="invalid-feedback d-block">{error}</div> : (column.helpText && <small className="text-muted mt-1 d-block">{column.helpText}</small>)}
                    </div>
                );
            default:
                const inputType = ['text', 'email', 'date', 'number', 'password', 'url', 'tel',].includes(type) ? type : 'text';
                return (
                    <div className="mb-4">
                        <PrimaryInput
                            label={column.label}
                            type={inputType as InputType}
                            placeholder={column.placeholder || `Enter ${column.label}`}
                            required={column.required}
                            value={value}
                            onChange={(e) => handleInputChange(identifier, e.target.value)}
                            className={error ? 'is-invalid' : ''}
                        />
                        {error ? <div className="invalid-feedback d-block" style={{ marginTop: '-0.75rem', marginBottom: '0.5rem' }}>{error}</div> : (column.helpText && <small className="text-muted mt-1 d-block" style={{ marginTop: '-1rem' }}>{column.helpText}</small>)}
                    </div>
                );
        }
    };

    if (isLoading) return <Layout><FullPageLoader /></Layout>;

    // Group columns by section if sections array is missing or empty
    const getGroupedSections = (): IFormSection[] => {
        if (formData?.sections && formData.sections.length > 0) {
            return [...formData.sections].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

        }
        if (formData?.columns && formData.columns.length > 0) {
            const grouped: { [key: string]: IFormColumn[] } = {};
            formData.columns.forEach((col) => {
                const sectionName = (col as IFormColumn).section || 'Default Section';

                if (!grouped[sectionName]) {
                    grouped[sectionName] = [];
                }
                grouped[sectionName].push(col);
            });

            return Object.entries(grouped).map(([name, columns], index) => ({
                id: `section-${index}`,
                name: name,
                sequence: index + 1,
                columns: columns.sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
            }));
        }

        return [];
    };

    const renderedSections = getGroupedSections();


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm(renderedSections)) {
            showToastNotification('danger', 'Please fill all required fields');
            return;
        }

        const payload = {
            formId: formData?.id || id,
            status: 'submitted',
            data: formValues
        };

        console.log("Submitting Payload:", JSON.stringify(payload, null, 2));

        try {
            const response = await FormService.formSubmission(payload as any);
            if (response?.status) {
                showToastNotification('success', response.message);
                // navigate('/hrm/forms');
            } else {
                console.error("Submission Failed:", response);
                showToastNotification('danger', response?.message || 'Failed to submit form');
            }
        } catch (error: unknown) {
            console.error("Submission Error:", error);
            showToastNotification('danger', (error as Error)?.message || 'Error submitting form');
        }
    };


    return (
        <Layout>
            <div className="form-view-container pb-5">
                <MainHeading
                    title={formData?.name || 'Form View'}
                    showbBackButton
                />

                <Container className="mt-4 px-0" style={{ maxWidth: '900px' }}>
                    {/* Form Header Info Card */}
                    <CardLayout cardStyle="mb-4 border-0 shadow-sm p-4 bg-white rounded-4 overflow-hidden position-relative">
                        <div className="position-absolute top-0 start-0 h-100 bg-primary" style={{ width: '6px' }}></div>
                        <Badge color="light" className="text-primary text-uppercase mb-3 px-3 py-2 fw-semibold letter-spacing-1">
                            {formData?.name || 'FORM'}
                        </Badge>
                        <h2 className="fw-bold mb-3 text-dark">{formData?.name || 'Untitled Form'}</h2>
                        {formData?.description && (
                            <p className="text-secondary fs-5 lh-base mb-4">
                                {formData.description}
                            </p>
                        )}
                        <Row className="text-muted small fw-medium">
                            <Col xs="auto" className="d-flex align-items-center me-4">
                                <i className="bi bi-calendar3 me-2"></i>
                                {formData?.createdAt ? moment(formData.createdAt).format('MMMM YYYY') : 'Period: N/A'}
                            </Col>
                            <Col xs="auto" className="d-flex align-items-center me-4">
                                <i className="bi bi-clock me-2"></i>
                                Est. 15-20 minutes
                            </Col>
                            <Col xs="auto" className="d-flex align-items-center">
                                <i className="bi bi-shield-lock me-2"></i>
                                Confidential
                            </Col>
                        </Row>
                    </CardLayout>

                    {/* Render Sections */}
                    {renderedSections.length > 0 ? (
                        renderedSections.map((section) => (
                            <CardLayout key={section.id} cardStyle="mb-4 border-0 shadow-sm p-4 bg-white rounded-4">
                                <div className="d-flex align-items-center mb-4 pb-2 border-bottom">
                                    <div className="section-icon-wrapper bg-primary-subtle p-2 rounded-3 me-3 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                                        <i className="bi bi-file-earmark-text text-primary fs-5"></i>
                                    </div>
                                    <h4 className="fw-bold mb-0 text-dark">{section.name}</h4>
                                </div>

                                <Row>
                                    {section?.columns?.map((column: IFormColumn) => (
                                        <Col key={column.id} lg={section?.columns?.length > 2 ? 6 : 12}>
                                            {renderColumn(column)}
                                        </Col>
                                    ))}
                                </Row>
                            </CardLayout>
                        ))
                    ) : (
                        <div className="text-center py-5 bg-white rounded-4 shadow-sm">
                            <p className="text-muted mb-0">No fields available for this form.</p>
                        </div>
                    )}

                    {/* Submit Button Placeholder to match design */}
                    <div className="text-center mt-5 mb-4 p-4 bg-white rounded-4 shadow-sm">
                        <p className="text-muted small mb-4">Review your answers before submitting. Your manager will receive a copy for the appraisal discussion.</p>
                        <button onClick={handleSubmit} className="btn btn-primary px-5 py-2 fw-semibold rounded-3 shadow-sm d-flex align-items-center mx-auto">
                            <i className="bi bi-check2-circle me-2 fs-5"></i>
                            Submit appraisal
                        </button>
                    </div>
                </Container>
            </div>

            <style>{`
                .letter-spacing-1 { letter-spacing: 1px; }
                .bg-primary-subtle { background-color: rgba(13, 110, 253, 0.1); }
                .lh-base { line-height: 1.6; }
                .letter-spacing-1 { letter-spacing: 0.5px; }
                .form-view-container .card-layout { 
                    transition: transform 0.2s ease-in-out;
                }
                .bi-calendar3, .bi-clock, .bi-shield-lock { color: #6c757d; }
            `}</style>
        </Layout>
    );
};

export default FormView;